import { useState, useEffect, useRef } from "react";
import {
  Box,
  TextContent,
  SpaceBetween,
  Table as CSDTable,
  Header,
  FormField,
  Badge,
  Spinner
} from "@cloudscape-design/components-themed";

import { COMMON_LABELS } from "../default-constants";
import { CONFIGS, LABELS } from "./constants";
import { showModal } from "../UI/modal/modal-slice";
import { useAppDispatch } from "../../app/hook";
import { useAsyncCallback } from "react-async-hook";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { Messages } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { formatLabel, formatYearDate } from "../../utils/format";
import {
  useGetTouristIndustriesQuery,
  useLazyGetTouristIndustriesDownloadQuery,
  usePostTouristIndustriesMutation,
  usePostTouristIndustriesUploadMutation,
} from "../../features/api";
import { IconButton } from "../UI/button/IconButton";

export interface NumberedTouristIndustries {
  no: number;
  registrationNumber: string;
  corporateNumber: string;
  companyName: string;
}

/*===================================================================================
 * 旅行業登録者定義照会・更新画面の一覧表
 * 概要：旅行業登録者定義照会・更新画面の一覧表を表示し、アップデートダウンロード機能を実装
 *
 * 前の画面からの必要なパラメーター
 * なし
 *
 * 遷移先：なし
 * 戻り先：なし
 ===================================================================================*/

const Table = () => {
  //変数宣言
  let createdAt = "";
  let createdBy = "";

  // ダウンロード関連変数
  const [lazyDownloadTouristIndustriesTrigger] = useLazyGetTouristIndustriesDownloadQuery();
  const dispatch = useAppDispatch();

  // spinner・文字列表示有無
  const [isDownloadSpinnerAndStrDisplay, setIsDownloadSpinnerAndStrDisplay] = useState<boolean>(false);

  // button表示有無
  const [isTouristIndustriesDownloadButton, setIsTouristIndustriesDownloadButton] = useState<boolean>(false);

  // アップロード関連変数
  const [uploadTouristIndustriesFile] =
    usePostTouristIndustriesUploadMutation();
  const [updateTouristIndustries] = usePostTouristIndustriesMutation();
  const inputRef = useRef<HTMLInputElement>(null);

  //各API呼び出し
  const { data, isFetching, refetch } = useGetTouristIndustriesQuery();

  // 行番号の追加
  const numberedData = data?.map((item, index) => ({
    ...item,
    no: index + 1,
  })) as NumberedTouristIndustries[];

  if (data !== undefined) {
    if (0 !== data.length) {
      createdAt = formatYearDate(data[0].createdAt);
      createdBy = data[0].createdBy;
    }
  }

  useEffect(() => {
    // モーダル閉じる場合にフラグを戻す
    refetch();
  }, [refetch]);

  /**
   * DLボタンを押下イベント関数
   */
  const onClickDownloadBtnHandler = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    executeDownload();
  };

  const executeDownload = async () => {
    // spinner・文字列活性
    setIsDownloadSpinnerAndStrDisplay(true);
    // button活性
    setIsTouristIndustriesDownloadButton(true);
    await lazyDownloadTouristIndustriesTrigger()
      .unwrap()
      .then()
      .finally(() => {
        // spinner・文字列非活性
        setIsDownloadSpinnerAndStrDisplay(false);
        // button非活性
        setIsTouristIndustriesDownloadButton(false);
      });
  };

  const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!(files && files[0])) {
      return;
    }

    // アップロードされたファイルの拡張子が「.csv」以外の場合はアラートを出して終了する。
    if (CONFIGS.FILE_EXTENSION !== getExt(files[0].name.toLowerCase())) {
      const messageId = "KGRERR008" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
      return;
    }

    const fileData = new FormData();
    fileData.append(CONFIGS.FILE, files[0]);

    // 旅行業登録者定義ファイルアップロードAPI呼び出し
    const checkData = await uploadTouristIndustriesFile(fileData).unwrap();

    if (CONFIGS.SUCCESS === checkData.status) {
      // モーダル表示処理
      let args: number[] = [];
      args = [...args, ...Object.values(checkData.checkResult)];
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.UPDATE,
          content: formatLabel(LABELS.UPLOAD_CONFIRM as string, args),
          // 旅行業登録者定義更新API呼び出し
          positiveFn: () =>
            updateTouristIndustries(checkData.updateData).then(() => refetch()),
        })
      );
    }

    /** 同じファイルを連続アップロードできるように処理 */
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  //ファイル名から拡張子を取得する関数
  function getExt(filename: string) {
    let pos = filename.lastIndexOf(".");
    if (pos === -1) return "";
    return filename.slice(pos);
  }

  /**
   * ULボタンを押下イベント関数
   */
  const uploadOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    inputRef.current?.click();
  };

  /**
   * アップロードファイルが選択されるイベント
   */
  const asyncEvent = useAsyncCallback(onChangeFile);

  return (
    <FormField stretch>
      {/* テーブル設定 */}
      <CSDTable
        columnDefinitions={[
          {
            id: "no",
            header: COMMON_LABELS.NO,
            width: 100,
            cell: (e: any) => e.no,
          },
          {
            id: "touristIndustriesNo",
            header: LABELS.TOURIST_INDUSTRIES_NO,
            width: 200,
            cell: (e: any) => e.registrationNumber,
          },
          {
            id: "corporateNumber",
            header: LABELS.CORPORATE_NUMBER,
            width: 200,
            cell: (e: any) => e.corporateNumber,
          },
          {
            id: "name",
            header: LABELS.COMPANY_NAME,
            cell: (e: any) => e.companyName,
          },
        ]}
        loading={isFetching}
        loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
        items={numberedData}
        resizableColumns
        stickyHeader
        totalItemsCount={13}
        header={
          /* 最終情報設定 */
          <SpaceBetween direction="vertical" size="xs">
            <Header
              variant="awsui-h1-sticky"
              actions={
                <SpaceBetween direction="vertical" size="xs">
                  <Badge color="green">
                    {LABELS.LAST_UPDATER}
                    {createdBy}/{LABELS.LAST_UPDATING_TIME}
                    {createdAt}
                  </Badge>
                  <SpaceBetween size={"xxl"} direction={"horizontal"}>
                    <IconButton
                      iconName={"upload"}
                      label={COMMON_LABELS.BTNS.UL}
                      onClick={uploadOnClick}
                    />
                    <SpaceBetween direction="horizontal" size="xs">
                      {isDownloadSpinnerAndStrDisplay ? <Spinner size="big" /> : <></>}
                      {isDownloadSpinnerAndStrDisplay ? <>{COMMON_LABELS.CONTENTS.DL_RUN}</> : <></>}
                      <IconButton
                        iconName={"download"}
                        label={COMMON_LABELS.BTNS.DL}
                        disabled={isTouristIndustriesDownloadButton}
                        onClick={onClickDownloadBtnHandler}
                      />
                    </SpaceBetween>
                    <input
                      hidden
                      ref={inputRef}
                      name="file"
                      type="file"
                      accept=".csv"
                      onChange={asyncEvent.execute}
                    />
                  </SpaceBetween>
                </SpaceBetween>
              }
            >
              {/* ボタン設定 */}
              <SpaceBetween direction="vertical" size="xs">
                <TextContent>
                  <h3>{LABELS.TITLE}</h3>
                </TextContent>
              </SpaceBetween>
            </Header>
          </SpaceBetween>
        }
        empty={
          // データがなかった場合の表示
          <Box textAlign="center" color="inherit">
            {COMMON_LABELS.CSD.NO_DATA}
          </Box>
        }
      />
    </FormField>
  );
};

export default Table;
